import { NgOptimizedImage } from '@angular/common';
import { Component, computed, effect, input, model, output, signal } from '@angular/core';

@Component({
  imports: [NgOptimizedImage],
  selector: 'app-slideshow',
  styleUrl: './slideshow.css',
  templateUrl: './slideshow.html',
})
export class Slideshow {
  public readonly galleryClicked = input(false);
  public readonly height = input('aspect-video');
  public readonly images = input.required<string[]>();
  public readonly openGalleryClicked = output<number>();

  public readonly selected = model(1);

  protected readonly class = computed(
    () => `relative m-auto touch-pan-y select-none ${this.height()}`,
  );
  protected readonly galleryClass = computed(
    () =>
      `w-full h-full cursor-pointer object-bottom ${this.galleryClicked() ? 'object-contain' : 'object-cover'} fade`,
  );
  protected readonly slideIndex = signal(1);
  protected readonly totalSlides = computed(() => this.images().length);

  private _activePointerId: null | number = null;
  private _swiped = false;
  private _touchStartX = 0;
  private _touchStartY = 0;

  constructor() {
    effect(() => {
      const _sel = this.selected();
      if (_sel !== 1) {
        this.slideIndex.set(_sel);
        this.selected.set(1);
      }
    });
  }

  public changeSlide(n: number): void {
    const _total = this.totalSlides();
    if (!_total) {
      return;
    }
    this.slideIndex.update((i) => {
      const _next = i + n;
      if (_next > _total) {
        return 1;
      }
      if (_next < 1) {
        return _total;
      }
      return _next;
    });
  }

  public onImageClick(): void {
    if (this._swiped) {
      this._swiped = false;
      return;
    }
    this.openGalleryClicked.emit(this.slideIndex());
  }

  public onPointerCancel(event: PointerEvent): void {
    if (event.pointerId === this._activePointerId) {
      this._activePointerId = null;
      this._swiped = false;
    }
  }

  public onPointerDown(event: PointerEvent): void {
    if (this._activePointerId !== null) {
      return;
    }
    // Skip touches starting near the screen edge so iOS/Android back gesture still works
    if (event.clientX <= 20 || event.clientX >= window.innerWidth - 20) {
      return;
    }
    this._activePointerId = event.pointerId;
    this._touchStartX = event.clientX;
    this._touchStartY = event.clientY;
    this._swiped = false;
  }

  public onPointerUp(event: PointerEvent): void {
    if (event.pointerId !== this._activePointerId) {
      return;
    }
    this._activePointerId = null;
    const _horizontalSwipe = event.clientX - this._touchStartX;
    const _verticalSwipe = event.clientY - this._touchStartY;

    if (Math.abs(_horizontalSwipe) >= 50 && Math.abs(_horizontalSwipe) > Math.abs(_verticalSwipe)) {
      this._swiped = true;
      this.changeSlide(_horizontalSwipe < 0 ? 1 : -1);
    }
  }
}
